import { Box, Button, IconButton, Typography } from '@mui/material';
import { Add } from '@mui/icons-material';
import { Category } from '../../types';
import { useCallback, useEffect, useRef } from 'react';
import DesignSystem from '../../designSystem';
import {
  CATEGORY_SUB_TILE_PX,
  CATEGORY_SUB_TILE_SLOT_PX,
  CATEGORY_TILE_PX,
  CATEGORY_TILE_SLOT_PX,
  categoryTileImageSx,
} from './categoryTileMetrics';

const TILE = CATEGORY_TILE_PX;
const SUB_TILE = CATEGORY_SUB_TILE_PX;
const TILE_SLOT = CATEGORY_TILE_SLOT_PX;
const SUB_TILE_SLOT = CATEGORY_SUB_TILE_SLOT_PX;

function mergeRefs<T>(...refs: Array<React.Ref<T> | undefined>) {
  return (node: T | null) => {
    refs.forEach((ref) => {
      if (!ref) return;
      if (typeof ref === 'function') ref(node);
      else (ref as React.MutableRefObject<T | null>).current = node;
    });
  };
}

const DRAG_THRESHOLD_PX = 5;

function useHorizontalWheelScroll(
  ref: React.RefObject<HTMLDivElement | null>,
  scrollKey: number
) {
  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const onWheel = (e: WheelEvent) => {
      if (el.scrollWidth <= el.clientWidth + 1) return;

      const delta = Math.abs(e.deltaX) > Math.abs(e.deltaY) ? e.deltaX : e.deltaY;
      if (delta === 0) return;

      const maxScroll = el.scrollWidth - el.clientWidth;
      const next = Math.min(maxScroll, Math.max(0, el.scrollLeft + delta));
      if (next === el.scrollLeft) return;

      e.preventDefault();
      e.stopPropagation();
      el.scrollLeft = next;
    };

    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, [ref, scrollKey]);
}

const CategoryContainer = ({
  children,
  containerRef,
  scrollKey,
  suppressTileClickRef,
}: {
  children: React.ReactNode;
  containerRef?: React.RefObject<HTMLDivElement | null>;
  scrollKey: number;
  suppressTileClickRef: React.MutableRefObject<boolean>;
}) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const dragRef = useRef({ pointerId: -1, startX: 0, scrollLeft: 0, moved: false });

  useHorizontalWheelScroll(scrollRef, scrollKey);

  const endDrag = useCallback((pointerId: number) => {
    const el = scrollRef.current;
    const drag = dragRef.current;
    if (!el || drag.pointerId !== pointerId) return;
    if (drag.moved) suppressTileClickRef.current = true;
    dragRef.current = { pointerId: -1, startX: 0, scrollLeft: 0, moved: false };
    try {
      el.releasePointerCapture(pointerId);
    } catch {
      /* already released */
    }
    el.style.cursor = '';
  }, [suppressTileClickRef]);

  const onPointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    if (e.button !== 0) return;
    const el = scrollRef.current;
    if (!el || el.scrollWidth <= el.clientWidth + 1) return;

    dragRef.current = {
      pointerId: e.pointerId,
      startX: e.clientX,
      scrollLeft: el.scrollLeft,
      moved: false,
    };
  };

  const onPointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    const el = scrollRef.current;
    const drag = dragRef.current;
    if (!el || drag.pointerId !== e.pointerId) return;

    const dx = e.clientX - drag.startX;
    if (!drag.moved && Math.abs(dx) < DRAG_THRESHOLD_PX) return;

    if (!drag.moved) {
      drag.moved = true;
      el.setPointerCapture(e.pointerId);
      el.style.cursor = 'grabbing';
    }

    e.preventDefault();
    el.scrollLeft = drag.scrollLeft - dx;
  };

  const onPointerUp = (e: React.PointerEvent<HTMLDivElement>) => {
    endDrag(e.pointerId);
  };

  return (
    <Box
      ref={mergeRefs(scrollRef, containerRef)}
      data-testid="category-scroll-row"
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerUp}
      sx={{
        width: '100%',
        maxWidth: '100%',
        minWidth: 0,
        overflowX: 'auto',
        overflowY: 'hidden',
        py: 0.25,
        scrollBehavior: 'auto',
        WebkitOverflowScrolling: 'touch',
        touchAction: 'pan-x',
        overscrollBehaviorX: 'contain',
        cursor: 'grab',
        userSelect: 'none',
        '& img': { WebkitUserDrag: 'none', userSelect: 'none' },
        '&::-webkit-scrollbar': { display: 'none' },
        scrollbarWidth: 'none',
      }}
    >
      <Box
        sx={{
          display: 'inline-flex',
          flexWrap: 'nowrap',
          alignItems: 'flex-start',
          gap: 1,
          width: 'max-content',
          minWidth: '100%',
          verticalAlign: 'top',
        }}
      >
        {children}
      </Box>
    </Box>
  );
};

interface CategoryListProps {
  categories: Category[];
  onSelectAll?: () => void;
  onDeselectAll?: () => void;
  onAddCategory?: () => void;
  addCategoryAriaLabel?: string;
  onSelect?: (categoryId: string | null, subcategoryId?: string | null, subsubcategoryId?: string | null) => void;
  onSubcategoryToggle?: (subcategoryId: string) => void;
  selectedCategoryId?: string | null;
  selectedSubcategoryId?: string | null;
  selectedSubsubcategoryId?: string | null;
  deselectedSubcategoryIds?: Set<number>;
}

/** iOS Home-style: small round bordered images, horizontal scroll. */
export default function CategoryList({
  categories,
  onSelectAll,
  onDeselectAll,
  onAddCategory,
  addCategoryAriaLabel = 'Manage categories',
  onSelect,
  onSubcategoryToggle,
  selectedCategoryId,
  selectedSubcategoryId,
  selectedSubsubcategoryId,
  deselectedSubcategoryIds = new Set(),
}: CategoryListProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const subcategoryContainerRef = useRef<HTMLDivElement>(null);
  const subsubcategoryContainerRef = useRef<HTMLDivElement>(null);
  const suppressTileClickRef = useRef(false);

  const runTileAction = (action: () => void) => {
    if (suppressTileClickRef.current) {
      suppressTileClickRef.current = false;
      return;
    }
    action();
  };

  const handleCategorySelect = (id: string) => {
    if (id === selectedCategoryId) {
      onSelect?.(null, null, null);
    } else {
      onSelect?.(id, null, null);
    }
  };

  const handleSubcategorySelect = (subcategoryId: string) => {
    if (onSubcategoryToggle) {
      onSubcategoryToggle(subcategoryId);
    } else if (subcategoryId === selectedSubcategoryId) {
      onSelect?.(selectedCategoryId ?? null, null, null);
    } else {
      onSelect?.(selectedCategoryId ?? null, subcategoryId, null);
    }
  };

  const handleSubsubcategorySelect = (subsubcategoryId: string) => {
    if (subsubcategoryId === selectedSubsubcategoryId) {
      onSelect?.(selectedCategoryId ?? null, selectedSubcategoryId ?? null, null);
    } else {
      onSelect?.(selectedCategoryId ?? null, selectedSubcategoryId ?? null, subsubcategoryId);
    }
  };

  const selectedCategory = categories.find((cat) => cat.id === selectedCategoryId);
  const selectedSubcategory = selectedCategory?.subcategories?.find(
    (sub) => sub.id === selectedSubcategoryId
  );

  const exclusiveMode = Boolean(selectedCategoryId);

  const renderTile = (
    key: string,
    name: string,
    imageUrl: string | undefined,
    active: boolean,
    dimmed: boolean,
    onClick: () => void,
    size: number
  ) => (
    <Box
      key={key}
      component="button"
      type="button"
      onClick={() => runTileAction(onClick)}
      aria-pressed={active}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 0.5,
        p: 0,
        border: 0,
        cursor: 'pointer',
        background: 'transparent',
        flexShrink: 0,
        width: size === TILE ? TILE_SLOT : SUB_TILE_SLOT,
        touchAction: 'pan-x',
        outline: 'none',
        WebkitTapHighlightColor: 'transparent',
        opacity: dimmed ? 0.3 : 1,
        transition: 'opacity 160ms ease',
        '&:focus-visible .cat-avatar': {
          boxShadow: `0 0 0 3px ${DesignSystem.colors.actionLight}`,
        },
      }}
    >
      <Box
        className="cat-avatar"
        sx={{
          width: size,
          height: size,
          borderRadius: '50%',
          overflow: 'hidden',
          backgroundColor: DesignSystem.colors.surfaceElevated,
          border: active
            ? `2px solid ${DesignSystem.colors.action}`
            : `1.5px solid ${DesignSystem.colors.separator}`,
          boxSizing: 'border-box',
          transition: 'border-color 160ms ease',
        }}
      >
        {imageUrl ? (
          <Box
            component="img"
            src={imageUrl}
            alt=""
            draggable={false}
            onDragStart={(e: React.DragEvent<HTMLImageElement>) => e.preventDefault()}
            onError={(e: React.SyntheticEvent<HTMLImageElement>) => {
              e.currentTarget.style.display = 'none';
            }}
            sx={categoryTileImageSx}
          />
        ) : (
          <Box
            sx={{
              width: '100%',
              height: '100%',
              display: 'grid',
              placeItems: 'center',
              color: DesignSystem.colors.textTertiary,
              fontSize: size * 0.36,
              fontWeight: 700,
            }}
          >
            {name.charAt(0)}
          </Box>
        )}
      </Box>
      <Typography
        component="span"
        title={name}
        sx={{
          width: '100%',
          color: DesignSystem.colors.textPrimary,
          fontSize: size === TILE ? 11 : 10,
          fontWeight: active ? 700 : 600,
          lineHeight: 1.15,
          textAlign: 'center',
          overflow: 'hidden',
          display: '-webkit-box',
          WebkitLineClamp: 2,
          WebkitBoxOrient: 'vertical',
          whiteSpace: 'normal',
          wordBreak: 'break-word',
          minHeight: size === TILE ? 24 : 20,
        }}
      >
        {name}
      </Typography>
    </Box>
  );

  return (
    <Box
      sx={{
        position: 'relative',
        width: '100%',
        minWidth: 0,
        mx: { xs: -2, md: -2.5 },
        px: { xs: 2, md: 2.5 },
      }}
    >
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'flex-end',
          alignItems: 'center',
          mb: 0.35,
          minHeight: 18,
        }}
      >
        {(selectedCategoryId || deselectedSubcategoryIds.size > 0) && (
          <Button
            variant="text"
            onClick={onDeselectAll || onSelectAll}
            sx={{
              color: DesignSystem.colors.textSecondary,
              fontWeight: 600,
              fontSize: 12,
              textTransform: 'none',
              minWidth: 0,
              py: 0,
              '&:hover': { color: DesignSystem.colors.textPrimary, backgroundColor: 'transparent' },
            }}
          >
            Clear
          </Button>
        )}
      </Box>

      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: 1,
          width: '100%',
          minWidth: 0,
        }}
      >
        <Box
          data-testid="category-add-static"
          sx={{
            flexShrink: 0,
            width: TILE_SLOT,
            display: 'flex',
            justifyContent: 'center',
          }}
        >
          <IconButton
            onClick={() => onAddCategory?.()}
            onPointerDown={(e) => e.stopPropagation()}
            aria-label={addCategoryAriaLabel}
            sx={{
              width: TILE,
              height: TILE,
              borderRadius: '50%',
              backgroundColor: DesignSystem.colors.surface,
              border: `1.5px dashed ${DesignSystem.colors.separator}`,
              '&:hover': { backgroundColor: DesignSystem.colors.surfaceElevated },
            }}
          >
            <Add sx={{ color: DesignSystem.colors.textPrimary, fontSize: 26 }} />
          </IconButton>
        </Box>

        <Box sx={{ flex: 1, minWidth: 0 }}>
          <CategoryContainer
            containerRef={containerRef}
            scrollKey={categories.length}
            suppressTileClickRef={suppressTileClickRef}
          >
            {categories.map((category) => {
              const selected = selectedCategoryId === category.id;
              const dimmed = exclusiveMode && !selected;
              return renderTile(
                category.id,
                category.name,
                category.imageUrl,
                selected,
                dimmed,
                () => handleCategorySelect(category.id),
                TILE
              );
            })}
          </CategoryContainer>
        </Box>
      </Box>

      {selectedCategory?.subcategories && selectedCategory.subcategories.length > 0 && (
        <Box sx={{ mt: 1 }}>
          <CategoryContainer
            containerRef={subcategoryContainerRef}
            scrollKey={selectedCategory.subcategories.length}
            suppressTileClickRef={suppressTileClickRef}
          >
            {selectedCategory.subcategories.map((sub) => {
              const faded = deselectedSubcategoryIds.has(Number(sub.id));
              const exclusiveSub =
                deselectedSubcategoryIds.size > 0 &&
                selectedCategory.subcategories!.length - deselectedSubcategoryIds.size === 1 &&
                !faded;
              const active = exclusiveSub || selectedSubcategoryId === sub.id;
              return renderTile(
                sub.id,
                sub.name,
                sub.imageUrl || selectedCategory.imageUrl,
                active,
                faded,
                () => handleSubcategorySelect(sub.id),
                SUB_TILE
              );
            })}
          </CategoryContainer>
        </Box>
      )}

      {selectedSubcategory?.subsubcategories && selectedSubcategory.subsubcategories.length > 0 && (
        <Box sx={{ mt: 1 }}>
          <CategoryContainer
            containerRef={subsubcategoryContainerRef}
            scrollKey={selectedSubcategory.subsubcategories.length}
            suppressTileClickRef={suppressTileClickRef}
          >
            {selectedSubcategory.subsubcategories.map((ss) => {
              const selected = selectedSubsubcategoryId === ss.id;
              return renderTile(
                ss.id,
                ss.name,
                ss.imageUrl || selectedCategory?.imageUrl,
                selected,
                Boolean(selectedSubsubcategoryId) && !selected,
                () => handleSubsubcategorySelect(ss.id),
                SUB_TILE
              );
            })}
          </CategoryContainer>
        </Box>
      )}
    </Box>
  );
}
